import React, { useRef, useState, useCallback } from "react";
import { Box, Typography, Button, Stack, Paper } from "@mui/material";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";

// Featured-image focal point picker + faithful multi-surface preview.
//
// The public hero and the index/category card thumbnails render the image
// with `background-size: cover`, whose crop differs per viewport. Crucially,
// the hero also paints a dark gradient + the post TITLE/category/byline over
// the image — so the focal point must be judged WITH that text, not against
// a bare crop. These previews therefore reproduce the real BlogHero overlay
// inside device chrome (a phone, a browser window) plus a real BlogCard
// thumbnail, so the author sees exactly what the reader will.
//
// State is the same "x,y" integer-percent string used end-to-end (DB → SSR),
// so what the author sets here is byte-for-byte what the renderer uses.

const clampPct = (n) => {
  if (!Number.isFinite(n)) return 50;
  return Math.min(100, Math.max(0, Math.round(n)));
};

// Total parse: any bad input → centre. Mirrors backend canblogFocus.ts and
// the SSR focusPosition.ts so all three agree on the default.
const parseFocus = (s) => {
  if (typeof s !== "string") return { x: 50, y: 50 };
  const parts = s.split(",");
  if (parts.length !== 2) return { x: 50, y: 50 };
  const x = Number(parts[0]);
  const y = Number(parts[1]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return { x: 50, y: 50 };
  return { x: clampPct(x), y: clampPct(y) };
};

// Same dark gradient BlogHero paints over the image for title legibility.
const HERO_OVERLAY =
  "linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.7) 100%)";

// A faux skeleton text line (placeholder page body under the hero).
const SkeletonLine = ({ w = "100%", h = 8 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: 0.5,
      bgcolor: "grey.300",
    }}
  />
);

// The real BlogHero, reproduced: cropped image + dark gradient + centred
// category chip / title / byline. `scale` tunes the type size for the frame.
function HeroSample({ imageUrl, x, y, title, categoryName, authorName, scale }) {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: `${x}% ${y}%`,
        backgroundRepeat: "no-repeat",
      }}
    >
      <Box sx={{ position: "absolute", inset: 0, background: HERO_OVERLAY }} />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: `${0.5 * scale}rem`,
          px: `${1.5 * scale}rem`,
          color: "#fff",
        }}
      >
        {categoryName ? (
          <Box
            sx={{
              fontSize: `${0.5 * scale}rem`,
              fontWeight: 700,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              px: `${0.6 * scale}rem`,
              py: `${0.2 * scale}rem`,
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.4)",
              bgcolor: "rgba(255,255,255,0.12)",
              lineHeight: 1.6,
            }}
          >
            {categoryName}
          </Box>
        ) : null}
        <Box
          sx={{
            fontSize: `${1.05 * scale}rem`,
            fontWeight: 800,
            lineHeight: 1.15,
            textShadow: "0 2px 6px rgba(0,0,0,0.35)",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {title || "Your post title"}
        </Box>
        {authorName ? (
          <Box
            sx={{
              fontSize: `${0.55 * scale}rem`,
              color: "rgba(255,255,255,0.85)",
            }}
          >
            By {authorName}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

// Browser-window chrome wrapping a wide desktop hero.
function DesktopPreview(props) {
  return (
    <Box
      sx={{
        borderRadius: 1.5,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: 2,
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.75,
          bgcolor: "grey.100",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={0.5}>
          {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
            <Box key={c} sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: c }} />
          ))}
        </Stack>
        <Box
          sx={{
            flex: 1,
            mx: 1,
            height: 16,
            borderRadius: 999,
            bgcolor: "grey.200",
          }}
        />
      </Box>
      <Box sx={{ width: "100%", aspectRatio: "1280 / 470" }}>
        <HeroSample {...props} scale={1} />
      </Box>
      <Stack spacing={0.6} sx={{ p: 1.5 }}>
        <SkeletonLine w="45%" h={9} />
        <SkeletonLine />
        <SkeletonLine />
        <SkeletonLine w="80%" />
      </Stack>
    </Box>
  );
}

// Phone device frame wrapping the mobile hero.
function PhonePreview(props) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 200,
        mx: "auto",
        borderRadius: 4,
        border: "8px solid #1c1c1e",
        bgcolor: "#1c1c1e",
        boxShadow: 3,
        overflow: "hidden",
      }}
    >
      <Box sx={{ position: "relative", bgcolor: "#fff", borderRadius: 1.5, overflow: "hidden" }}>
        {/* notch */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: 70,
            height: 14,
            bgcolor: "#1c1c1e",
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            zIndex: 1,
          }}
        />
        <Box sx={{ width: "100%", aspectRatio: "375 / 360" }}>
          <HeroSample {...props} scale={0.62} />
        </Box>
        <Stack spacing={0.5} sx={{ p: 1 }}>
          <SkeletonLine w="60%" h={7} />
          <SkeletonLine />
          <SkeletonLine />
          <SkeletonLine w="70%" />
        </Stack>
      </Box>
    </Box>
  );
}

// A real BlogCard thumbnail (image on top, no text overlay — text sits
// BELOW the image on the index/category pages).
function CardPreview({ imageUrl, x, y, title, categoryName }) {
  return (
    <Paper
      variant="outlined"
      sx={{ width: "100%", borderRadius: 1.5, overflow: "hidden" }}
    >
      <Box
        sx={{
          width: "100%",
          aspectRatio: "16 / 9",
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: `${x}% ${y}%`,
          backgroundRepeat: "no-repeat",
        }}
      />
      <Stack spacing={0.75} sx={{ p: 1.5 }}>
        {categoryName ? (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: "primary.main",
            }}
          >
            {categoryName}
          </Typography>
        ) : null}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            lineHeight: 1.3,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {title || "Your post title"}
        </Typography>
        <SkeletonLine />
        <SkeletonLine w="75%" />
      </Stack>
    </Paper>
  );
}

export default function FeaturedImageFocusPicker({
  imageUrl,
  value,
  onChange,
  title,
  categoryName,
  authorName,
}) {
  const { x, y } = parseFocus(value);
  const areaRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const emitFromEvent = useCallback(
    (clientX, clientY) => {
      const el = areaRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const nx = clampPct(((clientX - rect.left) / rect.width) * 100);
      const ny = clampPct(((clientY - rect.top) / rect.height) * 100);
      onChange(`${nx},${ny}`);
    },
    [onChange],
  );

  const handlePointerDown = (e) => {
    e.preventDefault();
    setDragging(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* setPointerCapture unsupported — drag still works via move events */
    }
    emitFromEvent(e.clientX, e.clientY);
  };

  const handlePointerMove = (e) => {
    if (!dragging) return;
    emitFromEvent(e.clientX, e.clientY);
  };

  const endDrag = () => setDragging(false);

  const sampleProps = { imageUrl, x, y, title, categoryName, authorName };

  return (
    <Box sx={{ mt: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="caption" color="text.secondary">
          Drag the marker to choose the part of the image that must stay visible.
        </Typography>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography variant="caption" color="text.secondary">
            {x}% / {y}%
          </Typography>
          <Button
            size="small"
            startIcon={<CenterFocusStrongIcon fontSize="small" />}
            onClick={() => onChange("50,50")}
            disabled={x === 50 && y === 50}
          >
            Reset to center
          </Button>
        </Stack>
      </Stack>

      {/* Interactive source image + draggable marker */}
      <Box
        ref={areaRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        sx={{
          position: "relative",
          width: "100%",
          maxHeight: 300,
          aspectRatio: "16 / 9",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          cursor: dragging ? "grabbing" : "crosshair",
          userSelect: "none",
          touchAction: "none",
          backgroundColor: "grey.100",
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: `${x}%`,
            top: `${y}%`,
            width: 28,
            height: 28,
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            border: "2px solid #fff",
            boxShadow: "0 0 0 2px rgba(0,0,0,0.5), 0 1px 4px rgba(0,0,0,0.4)",
            backgroundColor: "rgba(25,118,210,0.35)",
            pointerEvents: "none",
          }}
        />
      </Box>

      {/* Faithful previews — title text overlays the image exactly as the
          live hero does, so the author can see what the crop hides. */}
      <Typography
        variant="body2"
        sx={{ display: "block", mt: 2, mb: 1 }}
      >
        How it will look on different screens
      </Typography>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems="flex-start"
      >
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Desktop 
          </Typography>
          <DesktopPreview {...sampleProps} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Mobile 
          </Typography>
          <PhonePreview {...sampleProps} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Blog Homepage Cards
          </Typography>
          <CardPreview {...sampleProps} />
        </Box>
      </Stack>
    </Box>
  );
}
